import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { DatePickerField } from './ui/date-picker-field';
import { getCurrencySymbol } from '../lib/currency';
import {
  Send, Loader2, X, Plus, Trash2, Users, Check, AlertCircle,
  Mail, Clock, Eye, EyeOff, ChevronRight, RefreshCw, CalendarDays
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function generateCoverLetter(est) {
  const name = est?.estimate_name || 'the LMS Implementation';
  const org = est?.organization_name || 'your organization';
  const effort = (est?.total_effort || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const cost = (est?.total_cost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const symGl = getCurrencySymbol(est?.currency_code);
  return `Please find attached the Quote for ${name} prepared for ${org}.

This document outlines the proposed implementation scope, resource requirements, and projected investment of ${effort} hours (${symGl}${cost}) for your review and consideration.

Key sections include an executive summary, detailed cost and effort analysis, project timeline with resource allocation, and risk assessment.

We welcome the opportunity to discuss this proposal at your earliest convenience and address any questions you may have.`;
}

export default function SendStakeholdersDialog({ estimateId, estimate, onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [orgStakeholders, setOrgStakeholders] = useState([]);
  const [subject, setSubject] = useState(`Quote — ${estimate?.estimate_name || ''}`);
  const [coverLetter, setCoverLetter] = useState(() => generateCoverLetter(estimate));
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('');
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [benchmark, setBenchmark] = useState(null);
  const [factorsReviewed, setFactorsReviewed] = useState(false);
  const [ackNote, setAckNote] = useState('');
  const gated = (benchmark?.flags?.length || 0) > 0;
  const [justifications, setJustifications] = useState([]);
  useEffect(() => {
    if (!gated) return;
    axios.get(`${API_URL}/api/quote-gate/justifications`, { withCredentials: true })
      .then(res => setJustifications(res.data?.items || [])).catch(() => setJustifications([]));
  }, [gated]);

  // v387 — quote gate: project factors well above the portfolio average
  useEffect(() => {
    if (!estimateId) return;
    axios.get(`${API_URL}/api/estimates/${estimateId}/benchmark-check`, { withCredentials: true })
      .then(res => setBenchmark(res.data)).catch(() => setBenchmark(null));
  }, [estimateId]);

  // Fetch org stakeholders and send history
  useEffect(() => {
    const orgName = estimate?.organization_name;
    if (orgName) {
      // Find org ID from estimate data
      axios.get(`${API_URL}/api/organizations/lookups`, { withCredentials: true })
        .then(res => {
          const orgs = res.data?.organizations || [];
          const org = orgs.find(o => o.name === orgName);
          if (org?.org_id) {
            axios.get(`${API_URL}/api/organizations/${org.org_id}/stakeholders`, { withCredentials: true })
              .then(sRes => {
                const stkh = sRes.data?.stakeholders || [];
                setOrgStakeholders(stkh);
                if (stkh.length > 0) setRecipients(stkh.map(s => ({ ...s, selected: true })));
              }).catch(() => {});
          }
        }).catch(() => {});
    }
    // Fetch send history
    axios.get(`${API_URL}/api/export/quote/${estimateId}/send-history`, { withCredentials: true })
      .then(res => setHistory(res.data?.history || []))
      .catch(() => {});
  }, [estimateId, estimate?.organization_name]);

  const addRecipient = () => {
    if (!newEmail.trim()) return;
    setRecipients(prev => [...prev, { name: newName.trim(), email: newEmail.trim().toLowerCase(), role: newRole.trim(), selected: true }]);
    setNewEmail(''); setNewName(''); setNewRole('');
  };

  const toggleRecipient = (idx) => {
    setRecipients(prev => prev.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r));
  };

  const removeRecipient = (idx) => {
    setRecipients(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    const selected = recipients.filter(r => r.selected);
    if (selected.length === 0) return;
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/export/quote/${estimateId}/send`, {
        recipients: selected.map(r => ({ name: r.name, email: r.email, role: r.role })),
        subject,
        cover_letter: coverLetter,
        include_pdf: true,
        due_date: dueDate || null,
        benchmark_ack: gated ? { acknowledged: factorsReviewed, note: ackNote, flags: benchmark.flags, delta: benchmark.delta } : null,
      }, { withCredentials: true });
      setResult(res.data);
      // Refresh history
      const hRes = await axios.get(`${API_URL}/api/export/quote/${estimateId}/send-history`, { withCredentials: true });
      setHistory(hRes.data?.history || []);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Failed to send' });
    } finally { setSending(false); }
  };

  const refreshHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/export/quote/${estimateId}/send-history`, { withCredentials: true });
      setHistory(res.data?.history || []);
    } catch { /* */ }
  };

  const selectedCount = recipients.filter(r => r.selected).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="send-stakeholders-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="send-stakeholders-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-[#0F2040]">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[#4ABFCF]" />
            <h2 className="text-base font-bold text-white">Send to Stakeholders</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="send-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {benchmark?.flags?.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900" role="alert" data-testid="quote-gate-benchmark-warning">
              <AlertCircle className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-semibold">{benchmark.quoted ? 'Heads-up — this deal was previously quoted with' : 'Review before quoting —'} project factors well above the portfolio average</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {benchmark.flags.map(f => (
                    <span key={f.key} className="text-[10px] px-1.5 py-0 rounded border bg-white border-violet-200 text-violet-800" data-testid={`quote-gate-flag-${f.key}`}>
                      {f.label} ×{f.multiplier.toFixed(2)} <b>(+{f.vs_portfolio.toFixed(2)})</b> · avg ×{f.portfolio_avg_multiplier.toFixed(2)}
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-violet-700 mt-1">Flag threshold +{Number(benchmark.delta).toFixed(2)} across {benchmark.open_deals} open deals.</p>
                <label className="mt-2 flex items-start gap-2 cursor-pointer select-none" data-testid="quote-gate-ack-label">
                  <input type="checkbox" checked={factorsReviewed} onChange={(e) => setFactorsReviewed(e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 rounded text-violet-600 focus:ring-violet-500" data-testid="quote-gate-ack-checkbox" />
                  <span className="text-xs text-violet-900"><b>Factors reviewed</b> — I confirm these project factors are justified for this client. Required before sending; recorded in the send history with your name.</span>
                </label>
                {justifications.length > 0 && (
                  <select value={justifications.includes(ackNote) ? ackNote : ''} onChange={(e) => setAckNote(e.target.value)}
                    className="mt-1.5 w-full border border-violet-200 rounded-md px-2 py-1 text-xs bg-white text-violet-900 focus:ring-1 focus:ring-violet-400" data-testid="quote-gate-justification-select">
                    <option value="">Pick a saved justification…</option>
                    {justifications.map((j) => <option key={j} value={j}>{j}</option>)}
                  </select>
                )}
                <input type="text" value={ackNote} onChange={(e) => setAckNote(e.target.value.slice(0, 300))} placeholder={justifications.length ? 'or type your own justification' : 'Optional justification (e.g. client-mandated go-live date)'}
                  className="mt-1.5 w-full border border-violet-200 rounded-md px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-violet-400" data-testid="quote-gate-ack-note" />
              </div>
            </div>
          )}
          {/* Recipients */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5 mb-2">
              <Users className="w-3.5 h-3.5" /> Recipients
              {selectedCount > 0 && <Badge className="text-[9px] bg-[#4ABFCF]/10 text-[#4ABFCF]">{selectedCount} selected</Badge>}
            </label>

            {recipients.length > 0 && (
              <div className="space-y-1 mb-3 max-h-[140px] overflow-y-auto">
                {recipients.map((r, i) => (
                  <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    r.selected ? 'bg-[#4ABFCF]/5 border-[#4ABFCF]/30' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                    <input type="checkbox" checked={r.selected} onChange={() => toggleRecipient(i)}
                      className="w-3.5 h-3.5 rounded text-[#4ABFCF] focus:ring-[#4ABFCF]" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-[#0F2040] text-xs">{r.name || r.email}</span>
                      {r.name && <span className="text-slate-400 text-xs ml-1.5">{r.email}</span>}
                    </div>
                    {r.role && <Badge variant="outline" className="text-[8px] shrink-0">{r.role}</Badge>}
                    <button onClick={() => removeRecipient(i)} className="text-slate-300 hover:text-red-500 shrink-0">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add recipient row */}
            <div className="flex items-center gap-2">
              <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name"
                className="border rounded-md px-2 py-1.5 text-xs w-28 focus:ring-1 focus:ring-[#4ABFCF]" data-testid="send-add-name" />
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email *"
                onKeyDown={e => e.key === 'Enter' && addRecipient()}
                className="border rounded-md px-2 py-1.5 text-xs flex-1 focus:ring-1 focus:ring-[#4ABFCF]" data-testid="send-add-email" />
              <input type="text" value={newRole} onChange={e => setNewRole(e.target.value)} placeholder="Role"
                className="border rounded-md px-2 py-1.5 text-xs w-24 focus:ring-1 focus:ring-[#4ABFCF]" data-testid="send-add-role" />
              <Button size="sm" variant="outline" onClick={addRecipient} disabled={!newEmail.trim()}
                className="h-7 text-xs shrink-0" data-testid="send-add-btn">
                <Plus className="w-3 h-3" />
              </Button>
            </div>
            {orgStakeholders.length === 0 && recipients.length === 0 && (
              <p className="text-[10px] text-slate-400 mt-1.5">No stakeholders found for this organization. Add recipients above.</p>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5 block">Subject</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF]"
              data-testid="send-subject" />
          </div>

          {/* Cover Letter */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5 block">Cover Letter</label>
            <textarea value={coverLetter} onChange={e => setCoverLetter(e.target.value)}
              rows={7}
              className="w-full border rounded-md px-3 py-2 text-sm leading-relaxed focus:ring-1 focus:ring-[#4ABFCF] focus:border-[#4ABFCF] resize-y"
              data-testid="send-cover-letter" />
          </div>

          {/* Response Deadline */}
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5" /> Response Deadline <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <div className="flex items-center gap-3">
              <DatePickerField
                value={dueDate}
                onChange={setDueDate}
                testId="send-due-date"
                placeholder="Pick deadline"
                minDate={new Date().toISOString().split('T')[0]}
                widthClass="w-48"
              />
              {dueDate && (
                <button onClick={() => setDueDate('')} className="text-xs text-slate-400 hover:text-red-500 transition-colors" data-testid="send-due-date-clear">
                  Clear
                </button>
              )}
              {dueDate && (
                <span className="text-xs text-slate-500">
                  Stakeholders will see this deadline in their email
                </span>
              )}
            </div>
          </div>

          {/* Result */}
          {result && !result.error && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="send-result-success">
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700 font-medium">
                Sent to {result.sent}/{result.total} recipients
              </span>
            </div>
          )}
          {result?.error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="send-result-error">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-600">{result.error}</span>
            </div>
          )}

          {/* Send History */}
          <div data-testid="send-history-section">
            <button onClick={() => { setHistoryOpen(!historyOpen); if (!historyOpen) refreshHistory(); }}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              data-testid="send-history-toggle">
              <Clock className="w-3.5 h-3.5" />
              <span className="font-semibold">Send History</span>
              {history.length > 0 && <span className="bg-slate-200 text-slate-600 rounded-full px-1.5 text-[9px] font-semibold">{history.length}</span>}
              <ChevronRight className={`w-3 h-3 transition-transform ${historyOpen ? 'rotate-90' : ''}`} />
            </button>

            {historyOpen && (
              <div className="mt-2 space-y-1.5 max-h-[180px] overflow-y-auto" data-testid="send-history-list">
                {history.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No emails sent yet for this quote.</p>
                ) : (
                  <>
                    <div className="flex justify-end mb-1">
                      <button onClick={refreshHistory} className="text-[10px] text-[#4ABFCF] hover:underline flex items-center gap-1" data-testid="send-history-refresh">
                        <RefreshCw className="w-2.5 h-2.5" /> Refresh
                      </button>
                    </div>
                    {history.map(h => (
                      <div key={h.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-xs" data-testid={`send-history-item-${h.id}`}>
                        <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-[#0F2040] truncate">{h.recipient_name || h.recipient_email}</span>
                            {h.recipient_role && <Badge variant="outline" className="text-[7px]">{h.recipient_role}</Badge>}
                          </div>
                          <p className="text-[9px] text-slate-400 truncate">{h.recipient_email}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {h.benchmark_ack?.acknowledged && (
                            <Badge className="text-[8px] bg-violet-100 text-violet-700 border-violet-200 flex items-center gap-0.5"
                              title={`Factors reviewed by ${h.benchmark_ack.by_name || h.benchmark_ack.by}${h.benchmark_ack.at ? ` on ${new Date(h.benchmark_ack.at).toLocaleString()}` : ''}: ${(h.benchmark_ack.flags || []).map(f => `${f.label} ×${Number(f.multiplier).toFixed(2)}`).join(', ')}${h.benchmark_ack.note ? ` — ${h.benchmark_ack.note}` : ''}`}
                              data-testid={`send-history-ack-${h.id}`}>
                              <Check className="w-2.5 h-2.5" /> Factors reviewed
                            </Badge>
                          )}
                          {h.opened ? (
                            <Badge className="text-[8px] bg-emerald-100 text-emerald-700 border-emerald-200 flex items-center gap-0.5">
                              <Eye className="w-2.5 h-2.5" /> Opened
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[8px] text-slate-400 flex items-center gap-0.5">
                              <EyeOff className="w-2.5 h-2.5" /> Unopened
                            </Badge>
                          )}
                        </div>
                        <span className="text-[9px] text-slate-400 shrink-0">
                          {h.sent_at ? new Date(h.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-slate-50">
          <p className="text-[10px] text-slate-400">PDF will be generated and attached automatically</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="text-xs" data-testid="send-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSend} disabled={sending || selectedCount === 0 || (gated && !factorsReviewed)}
              title={gated && !factorsReviewed ? 'Tick “Factors reviewed” above to enable sending' : undefined}
              className="text-xs bg-[#4ABFCF] hover:bg-[#3AAFBF] text-white" data-testid="send-confirm-btn">
              {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
              {sending ? 'Sending...' : `Send to ${selectedCount} Recipient${selectedCount !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
